import { defineStore } from 'pinia'
import { sendRealtimeMessageAPI } from '@/services/results-service'
import { useUserStore } from '@/stores/user-store'
import { useStatesStore } from './states-store'

export const useRealtimeStore = defineStore('realtime-store', {
  state: () => ({
    error: null,
    loading: false,
    messages: {},
    connectionId: null,
    waitingForResponse: false,
    waitingForResponseSince: null,
    currentSessionId: Date.now(),
    section: null,
    storageUpdateTimer: null,
    pendingStorageUpdate: false,
    targetSessionId: null // Track which session we're saving to (null = current session)
  }),
  getters: {
    storageKeyForSession:
      () =>
      ({ sessionId, section }) => {
        const {
          organizationName,
          surveyName,
          selectedGroup,
          selectedAttributeSegment
        } = useStatesStore()
        if (!section) {
          throw new Error('Section is required for storage key')
        }
        const groupName =
          section === 'groupsComparison' ? 'ALL' : selectedGroup?.name

        let attributeSegment = 'ALL'
        if (selectedAttributeSegment && section !== 'groupsComparison') {
          attributeSegment = selectedAttributeSegment.name
        }
        return `realtime-messages-${organizationName}-${surveyName}-${groupName}-${section}-${attributeSegment}-${sessionId}`
      },
    realtimeEnabled: () => {
      const userStore = useUserStore()
      const statesStore = useStatesStore()
      try {
        const isAdmin = userStore.userIsGroupAdmin || userStore.userIsPulsAdmin
        const pulsAiFeatureEnabled =
          statesStore.organizationFeatures?.pulsAI?.enabled
        return isAdmin && pulsAiFeatureEnabled
      } catch (e) {
        console.error(e)
        return false
      }
    }
  },
  actions: {
    clear() {
      this.setRealtimeError(null)
      this.loading = false
      this.messages = {}
      this.currentSessionId = Date.now()
    },
    setSection({ section }) {
      this.section = section
    },
    updateStoredMessagesForSession({ messages, currentSessionId }) {
      try {
        const key = this.storageKeyForSession({
          sessionId: currentSessionId,
          section: this.section
        })
        console.log(key)
        localStorage.setItem(key, JSON.stringify(messages))
      } catch (e) {
        if (e instanceof DOMException && e.name === 'QuotaExceededError') {
          console.error(
            'Local storage quota exceeded. Unable to save messages. Clearing and trying again.'
          )
          this.clearAllStoredMessages()
          // try again after clearing
          localStorage.setItem(
            this.storageKeyForSession({
              sessionId: currentSessionId,
              section: this.section
            }),
            JSON.stringify(messages)
          )
        } else {
          throw e
        }
      }
    },
    scheduleStorageUpdate() {
      // Clear existing timer if one is already scheduled
      if (this.storageUpdateTimer) {
        clearTimeout(this.storageUpdateTimer)
      }

      // Mark that we have a pending update
      this.pendingStorageUpdate = true

      // Schedule the update to happen in 2 seconds
      this.storageUpdateTimer = setTimeout(() => {
        this.storageUpdateTimer = null
        this.pendingStorageUpdate = false

        // Perform the actual storage update
        try {
          // If targetSessionId is set, save to that specific session instead
          const sessionToSave = this.targetSessionId || this.currentSessionId
          this.updateStoredMessagesForSession({
            messages: this.messages,
            currentSessionId: sessionToSave
          })
        } catch (error) {
          this.error = error
          console.error('Failed to update stored messages:', error)
        }
      }, 2000)
    },
    addRealtimeMessageAction({ message }) {
      const modelName = message.modelName || 'none'
      if (!this.messages[modelName]) {
        this.messages[modelName] = []
      }

      try {
        // if there is existing message with same order as current message, then update it and concatenate the text property
        if (message.direction === 'in') {
          this.waitingForResponse = false
          this.waitingForResponseSince = null
        }

        if (message.direction === 'out') {
          this.waitingForResponse = true
          this.waitingForResponseSince = new Date()
        }

        const existingMessage = this.messages[modelName].find(
          (m) => m.order === message.order
        )

        if (existingMessage) {
          existingMessage.text = `${existingMessage.text}${message.text}`
          const updatedMessages = this.messages[modelName].map((m) => {
            if (m.order === message.order) {
              return existingMessage
            }
            return m
          })
          this.messages[modelName] = updatedMessages
        } else {
          if (modelName !== 'compare') {
            if (this.messages.compare) {
              // if we have sent an initial compare messaage, we prepend that to the messages
              this.messages[modelName].unshift(this.messages.compare[0])
            }
          }
          this.messages[modelName].push(message)
        }

        // If first message in this session, save immediately to ensure it's not lost
        // if (this.messages[modelName].length === 1) {
        //   const sessionToSave = this.targetSessionId || this.currentSessionId
        //   this.updateStoredMessagesForSession({
        //     messages: this.messages,
        //     currentSessionId: sessionToSave
        //   })
        // } else {
        // Schedule storage update for subsequent messages
        this.scheduleStorageUpdate()
        // }
      } catch (error) {
        this.error = error
        throw error
      }
    },

    migrateOldStoredMessages() {
      // legacy format:  `realtime-messages-${organizationName}-${surveyName}-${section}-${sessionId}`
      try {
        const prefix = 'realtime-messages-'
        const keys = Object.keys(localStorage).filter(
          (key) => key.startsWith(prefix) && key.split('-').length === 6
        )
        keys.forEach((key) => {
          const parts = key.split('-')
          const attributeSegment = 'ALL'
          const keyWithGroupName = `realtime-messages-${parts[2]}-${parts[3]}-${parts[2]}-${parts[4]}-${attributeSegment}-${parts[5]}`
          console.log(`Migrating ${key} to ${keyWithGroupName}`)
          const item = localStorage.getItem(key)
          localStorage.setItem(keyWithGroupName, item)
          localStorage.removeItem(key)
        })
      } catch (error) {
        console.error(error)
      }
    },
    loadStoredMessagesFromSession({ sessionId }) {
      try {
        const prefix = this.storageKeyForSession({
          sessionId: '',
          section: this.section
        })
        const key = sessionId.startsWith(prefix)
          ? sessionId
          : this.storageKeyForSession({ sessionId, section: this.section })
        const storedMessages = localStorage.getItem(key)
        if (storedMessages) {
          this.messages = JSON.parse(storedMessages)
          this.currentSessionId = key.replace(prefix, '')
        } else {
          this.messages = {}
        }
      } catch (error) {
        this.error = error
        throw error
      }
    },
    clearAllStoredMessages() {
      try {
        const prefix = this.storageKeyForSession({
          sessionId: '',
          section: this.section
        })
        const keys = Object.keys(localStorage).filter((key) =>
          key.startsWith(prefix)
        )
        keys.forEach((key) => localStorage.removeItem(key))
      } catch (error) {
        this.error = error
        throw error
      }
    },
    getStoredMessages() {
      // get all keys of all items in localStorage that start with 'realtime-messages-'
      this.migrateOldStoredMessages()
      const keyPrefix = this.storageKeyForSession({
        sessionId: '',
        section: this.section
      })
      const keys = Object.keys(localStorage)
        .filter((key) => key.startsWith(keyPrefix))
        .sort((a, b) => {
          // sort by timestamp
          const aTimestamp = parseInt(a.split(keyPrefix)[1])
          const bTimestamp = parseInt(b.split(keyPrefix)[1])
          return bTimestamp - aTimestamp // descending order
        })
        .filter((id) => {
          // Filter out invalid/corrupted entries
          try {
            const messages = JSON.parse(localStorage.getItem(id))
            const modelKey = Object.keys(messages)[0]
            return (
              modelKey && messages[modelKey] && messages[modelKey].length > 0
            )
          } catch (e) {
            console.warn(`Corrupted localStorage entry: ${id}`, e)
            return false
          }
        })
        .map((id) => {
          const messages = JSON.parse(localStorage.getItem(id))
          const modelKey = Object.keys(messages)[0]
          const firstMessage = messages[modelKey][0]
          const formattedDate = new Date(
            firstMessage.timestamp
          ).toLocaleString()
          const maxLength = 50 // max length of title
          const length = firstMessage.text.length
          const title =
            firstMessage.text.slice(0, maxLength) +
            (length > maxLength ? '...' : '')
          return {
            id,
            title,
            formattedDate
          }
        })
      return keys
    },
    findStoredMessagesByTag(tag) {
      // Find all stored message sessions that contain a message with the specified tag
      const keyPrefix = this.storageKeyForSession({
        sessionId: '',
        section: this.section
      })
      const matchingSessions = []

      const keys = Object.keys(localStorage)
        .filter((key) => key.startsWith(keyPrefix))
        .sort((a, b) => {
          // sort by timestamp, most recent first
          const aTimestamp = parseInt(a.split(keyPrefix)[1])
          const bTimestamp = parseInt(b.split(keyPrefix)[1])
          return bTimestamp - aTimestamp
        })

      for (const key of keys) {
        try {
          const messages = JSON.parse(localStorage.getItem(key))
          const modelKey = Object.keys(messages)[0]

          if (!modelKey || !messages[modelKey]) continue

          // Check if any message in this session has the tag
          const hasTag = messages[modelKey].some(
            (m) => m.tags && m.tags.includes(tag)
          )

          if (hasTag) {
            matchingSessions.push({
              sessionId: key,
              messages: messages
            })
          }
        } catch (e) {
          console.warn(`Error checking stored messages for tag: ${tag}`, e)
        }
      }

      return matchingSessions
    },
    getMessagesFromSpecificSession(sessionId) {
      // Fetch messages from a specific session without changing the active session
      // This is used by widgets that need their own dedicated session
      try {
        const storageKey = this.storageKeyForSession({
          sessionId,
          section: this.section
        })
        const storedMessages = localStorage.getItem(storageKey)
        if (storedMessages) {
          return JSON.parse(storedMessages)
        }
        return {}
      } catch (error) {
        console.error('Failed to get messages from specific session:', error)
        return {}
      }
    },
    updateStoredMessagesForSpecificSession({ sessionId, messages, tries = 0 }) {
      // Update messages for a specific session without changing the active session
      // This is used by widgets that need their own dedicated session
      try {
        const storageKey = this.storageKeyForSession({
          sessionId,
          section: this.section
        })
        localStorage.setItem(storageKey, JSON.stringify(messages))
      } catch (e) {
        if (
          e instanceof DOMException &&
          e.name === 'QuotaExceededError' &&
          tries < 1
        ) {
          console.error(
            'Local storage quota exceeded. Unable to save messages for specific session. Clearing and trying again.'
          )
          this.clearAllStoredMessages()
          // try again after clearing
          const storageKey = this.storageKeyForSession({
            sessionId,
            section: this.section,
            tries: tries + 1
          })
          localStorage.setItem(storageKey, JSON.stringify(messages))
        } else {
          throw e
        }
      }
    },
    async sendRealtimeMessageActionWithSessionId({
      sessionId,
      surveyName,
      groupName,
      message,
      aggregate,
      surveyData
    }) {
      // Send message and save to a specific session
      // This is used by widgets that need their own dedicated session
      // This will set the target session so that WebSocket responses also get saved there
      const connectionId = this.connectionId

      try {
        // Set the target session for this message and all its responses
        this.currentSessionId = sessionId
        this.targetSessionId = sessionId

        this.loading = true
        this.addRealtimeMessageAction({ message })

        const messages = this.messages[message.modelName] || []
        messages[0].surveyData = surveyData

        const res = await sendRealtimeMessageAPI({
          connectionId,
          surveyName,
          groupName,
          messages,
          aggregate
        })

        return res
      } catch (error) {
        this.popRealtimeMessageAction()
        this.resetRealtimeWaiting()
        const err = error.message || error
        this.setRealtimeError(err)
        throw error
      } finally {
        this.loading = false
        // Keep targetSessionId set so WebSocket responses also go here
        // Don't clear it - widget needs all responses in its session
      }
    },
    popRealtimeMessageAction() {
      try {
        Object.keys(this.messages).forEach((modelName) => {
          this.messages[modelName].pop()
        })
      } catch (error) {
        this.error = error
        throw error
      }
    },
    resetRealtimeWaiting() {
      try {
        this.waitingForResponse = false
        this.waitingForResponseSince = null
      } catch (error) {
        this.error = error
        throw error
      }
    },
    setRealtimeError(error) {
      this.error = error
    },
    async sendRealtimeMessageAction({
      surveyName,
      groupName,
      message,
      aggregate,
      surveyData
    }) {
      const connectionId = this.connectionId // + 'failme' // uncomment for testing error handling
      this.loading = true
      this.addRealtimeMessageAction({ message })

      const messages = this.messages[message.modelName] || []
      messages[0].surveyData = surveyData
      // if (!message.modelName !== 'compare' && this.messages.compare) {
      //   // if we have sent an initial compare messaage, we prepend that to the messages
      //   messages.unshift(this.messages.compare[0])
      // }
      try {
        const res = await sendRealtimeMessageAPI({
          connectionId,
          surveyName,
          groupName,
          messages,
          aggregate
        })
      } catch (error) {
        this.popRealtimeMessageAction()
        this.resetRealtimeWaiting()
        const err = error.message || error
        this.setRealtimeError(err)
        throw error
      } finally {
        this.loading = false
      }
    },
    setRealtimeConnectionId({ connectionId }) {
      this.connectionId = connectionId
    }
  }
})
